import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion, type Variants } from 'framer-motion';
import { X, AlertCircle, Loader2, Ban } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useT } from '@/i18n';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { cn } from '@/lib/utils';
import { toast } from '@/stores/toast.store';

export type IneligibilityReason =
  | 'moved_out_of_ny'
  | 'moved_out_of_area'
  | 'changed_insurance_plan'
  | 'deceased'
  | 'wrong_contact_info';

const REASONS: ReadonlyArray<{ key: IneligibilityReason; labelKey: string }> = [
  { key: 'moved_out_of_ny',        labelKey: 'ineligibility.reason.moved_out_of_ny' },
  { key: 'moved_out_of_area',      labelKey: 'ineligibility.reason.moved_out_of_area' },
  { key: 'changed_insurance_plan', labelKey: 'ineligibility.reason.changed_insurance_plan' },
  { key: 'deceased',               labelKey: 'ineligibility.reason.deceased' },
  { key: 'wrong_contact_info',     labelKey: 'ineligibility.reason.wrong_contact_info' },
];

/**
 * v0.5.1 BUG-2/4 — modal overlay for marking a client as ineligible
 * during PRE-CALL. Mirrors DeclineModal pattern (Cluster H Fase 2):
 *   - createPortal to document.body
 *   - AnimatePresence + framer-motion (respects prefers-reduced-motion)
 *   - Focus trap (Tab/Shift+Tab cycles within modal)
 *   - ESC + backdrop click close
 *
 * 5 preset radio reasons cover both BUG-2 ("no cumple requisitos") and
 * BUG-4 ("moved out of NY") merged. Submit calls onConfirm(reason)
 * which the caller (PreCallPage) wires to the call-attempts endpoint
 * with outcome='client_ineligible'.
 */
export interface IneligibilityModalProps {
  /** When true the modal mounts. Caller controls open state. */
  open: boolean;
  /** Display name for the client being marked (header subtitle). */
  clientName?: string;
  /** Closes the modal (cancel path). */
  onClose: () => void;
  /** Async submitter — parent owns the mutation. Resolves on success;
   *  reject on error (modal surfaces a toast). */
  onConfirm: (reason: IneligibilityReason) => Promise<void>;
}

export function IneligibilityModal({ open, clientName, onClose, onConfirm }: IneligibilityModalProps): JSX.Element {
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open ? <IneligibilityModalInner clientName={clientName} onClose={onClose} onConfirm={onConfirm} /> : null}
    </AnimatePresence>,
    document.body,
  );
}

interface InnerProps {
  clientName?: string;
  onClose: () => void;
  onConfirm: (reason: IneligibilityReason) => Promise<void>;
}

function IneligibilityModalInner({ clientName, onClose, onConfirm }: InnerProps): JSX.Element {
  const t = useT();
  const reducedMotion = useReducedMotion();
  const dialogRef = useRef<HTMLDivElement>(null);

  const [reason, setReason] = useState<IneligibilityReason | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const titleId = 'ineligibility-modal-title';

  // Focus trap
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'input,button,textarea,select,[tabindex]:not([tabindex="-1"])'
      );
      const list = Array.from(focusables).filter((el) => !el.hasAttribute('disabled'));
      if (list.length === 0) return;
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const canSubmit = reason !== '' && !submitting;

  const handleSubmit = async (): Promise<void> => {
    if (!reason) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason);
      toast.success(t('ineligibility.toast.success'));
      onClose();
    } catch (err) {
      const msg = (err as Error)?.message || String(err);
      setError(msg);
      toast.error(t('ineligibility.toast.failed'), msg);
    } finally {
      setSubmitting(false);
    }
  };

  const backdrop: Variants = reducedMotion
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.15 } },
      };
  const modalVariants: Variants = reducedMotion
    ? { hidden: { opacity: 1, scale: 1 }, visible: { opacity: 1, scale: 1 }, exit: { opacity: 1, scale: 1 } }
    : {
        hidden: { opacity: 0, scale: 0.96 },
        visible: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 300, damping: 25 } },
        exit: { opacity: 0, scale: 0.98, transition: { duration: 0.15 } },
      };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8">
      <motion.div
        className="absolute inset-0 bg-ink-900/50 backdrop-blur-sm"
        variants={backdrop}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative bg-surface rounded-xl shadow-elevated w-full max-w-md max-h-[85vh] overflow-y-auto"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <header className="sticky top-0 bg-surface border-b border-ink-200 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="flex items-start gap-2.5">
            <Ban className="w-5 h-5 mt-0.5 text-rose-600 shrink-0" aria-hidden />
            <div>
              <h2 id={titleId} className="text-base font-semibold text-ink-900">
                {t('ineligibility.modal.title')}
              </h2>
              {clientName && (
                <p className="text-xs text-ink-500 mt-0.5">{clientName}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="text-ink-500 hover:text-ink-900 p-1 -mt-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <p className="text-sm text-ink-700">{t('ineligibility.modal.body')}</p>

          <fieldset className="space-y-2">
            <legend className="sr-only">{t('ineligibility.modal.reasonLegend')}</legend>
            {REASONS.map((r) => (
              <label
                key={r.key}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors',
                  reason === r.key
                    ? 'border-rose-400 bg-rose-50 text-rose-900'
                    : 'border-ink-200 bg-surface hover:bg-ink-50'
                )}
              >
                <input
                  type="radio"
                  name="ineligibility-reason"
                  value={r.key}
                  checked={reason === r.key}
                  onChange={() => setReason(r.key)}
                  className="w-4 h-4 text-rose-600 focus:ring-rose-400"
                />
                <span className="text-sm font-medium">{t(r.labelKey as never)}</span>
              </label>
            ))}
          </fieldset>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-surface border-t border-ink-200 px-5 py-3 flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden />}
            {t('ineligibility.modal.submit')}
          </Button>
        </footer>
      </motion.div>
    </div>
  );
}
